import type { QueryClient } from "@tanstack/react-query";
import { queryKeys } from "~/lib/query-keys";
import { getBrowserPlatformApiClient } from "~/lib/trpc/browser-client";
import type { PlatformApiTrpcClient } from "~/lib/trpc/platform-api-adapter";
import type { AssetDetailsOutput } from "./data/asset-domain-contract";
import type { AssetUniverseCanonicalGroup } from "./data/asset-universe-contract";
import {
  seedAssetDomainFromAssetDetails,
  seedAssetUniverseFromCanonicalGroups,
} from "./data/db/collections";
import { normalizeAssetDomainAssetIds } from "./data/db/models";

export function getAssetDomainAssetDetailsQueryOptions(input: {
  assetId: string;
  platformApi?: PlatformApiTrpcClient;
  sessionScopeToken?: string;
}) {
  const platformApi = input.platformApi ?? getBrowserPlatformApiClient();

  return {
    queryKey: queryKeys.asset.detailsById(input.assetId),
    queryFn: async () => {
      const response = await platformApi.assets.getAssetDetails.query({
        assetId: input.assetId,
      });
      await seedAssetDomainFromAssetDetails(input.assetId, response, {
        sessionScopeToken: input.sessionScopeToken,
      });
      return response;
    },
    retry: false,
    staleTime: 30_000,
  } as const;
}

export async function ensureAssetDomainAssetDetailsData(input: {
  assetId: string;
  platformApi?: PlatformApiTrpcClient;
  queryClient: QueryClient;
  sessionScopeToken?: string;
}): Promise<AssetDetailsOutput> {
  const response = await input.queryClient.ensureQueryData(
    getAssetDomainAssetDetailsQueryOptions({
      assetId: input.assetId,
      platformApi: input.platformApi,
      sessionScopeToken: input.sessionScopeToken,
    }),
  );

  await seedAssetDomainFromAssetDetails(input.assetId, response, {
    sessionScopeToken: input.sessionScopeToken,
  });

  return response;
}

export function collectAssetUniverseGroupRouteAssetIds(
  group: AssetUniverseCanonicalGroup | null | undefined,
): string[] {
  if (!group) {
    return [];
  }

  const assetIds: string[] = [];
  const issuedProducts = Array.isArray(group.issuedProducts)
    ? group.issuedProducts
    : [];
  const tokenRepresentations = Array.isArray(group.tokenRepresentations)
    ? group.tokenRepresentations
    : [];

  for (const product of issuedProducts) {
    const assets = Array.isArray(product.assets) ? product.assets : [];
    for (const asset of assets) {
      assetIds.push(asset.assetId);
    }
  }

  for (const token of tokenRepresentations) {
    assetIds.push(token.assetId);
  }

  return normalizeAssetDomainAssetIds(assetIds);
}

function canSeedAssetUniverseGroup(
  group: AssetUniverseCanonicalGroup,
): boolean {
  return (
    typeof group.underlying.assetCanonicalId === "string" &&
    group.underlying.assetCanonicalId.trim().length > 0 &&
    Array.isArray(group.issuedProducts) &&
    Array.isArray(group.tokenRepresentations)
  );
}

export async function seedAssetUniverseGroupRoutePreload(
  group: AssetUniverseCanonicalGroup | null | undefined,
  options?: {
    sessionScopeToken?: string;
  },
): Promise<string[]> {
  if (!group) {
    return [];
  }

  if (!canSeedAssetUniverseGroup(group)) {
    return collectAssetUniverseGroupRouteAssetIds(group);
  }

  await seedAssetUniverseFromCanonicalGroups([group], {
    sessionScopeToken: options?.sessionScopeToken,
  });

  return collectAssetUniverseGroupRouteAssetIds(group);
}
